import { acceptHMRUpdate, defineStore } from "pinia";
import { normalizeIdentity } from "@/helpers/utility/Id";

import { useModelStore } from "@editor/items/model/model.store";
import type { CanvasSelection } from "@editor/net/NetCanvasProps";
import type { Identity, PartialIdentity, Uid } from "@/helpers/utility/Id";

export type SelectableType =
  | "resource"
  | "area"
  | "target"
  | "relation"
  | "flow"
  | "flowBranch"
  | "momentum"
  | "question"
  | "index"
  | "sceneNode";

const defaultSelected = (): {
  [k in SelectableType]: number[];
} => ({
  resource: [],
  area: [],
  target: [],
  relation: [],
  flow: [],
  flowBranch: [],
  momentum: [],
  question: [],
  index: [],
  sceneNode: []
});

export type HighlightableType =
  | "momentum"
  | "target"
  | "relation"
  | "flow"
  | "question"
  | "area"
  | "resource"
  | "action"
  | "incident"
  | "index"
  | "sceneNode";

const defaultHighlights = (): { [k in HighlightableType]: number[] } => ({
  momentum: [],
  target: [],
  relation: [],
  flow: [],
  question: [],
  area: [],
  resource: [],
  action: [],
  incident: [],
  index: [],
  sceneNode: []
});

export const useSelectionStore = defineStore("model/selection", {
  state: () => ({
    selected: defaultSelected(),
    highlighted: defaultHighlights(),

    /**
     * A single selected item that is subordered to the main selection
     */
    subSelection: null as {
      type: "stakeholder" | "relation" | "flow" | "flowBranch" | "momentum";
      id: number;
    } | null,

    subSubSelection: null as {
      type: "flowBranch";
      id: number;
    } | null,

    selectedStakeholderIds: [] as number[]
  }),
  getters: {
    selection(): CanvasSelection {
      return {
        selected: this.selectedUids,
        highlighted: this.highlightedUids,
        active: new Set<string>()
      };
    },
    selectedUids(state): Set<Uid<SelectableType>> {
      const uids = new Set<Uid<SelectableType>>();
      for (const type in state.selected) {
        const t = type as SelectableType;
        if (state.selected[t] == null) continue;
        for (const id of state.selected[t]) {
          uids.add(`${type}_${id}` as Uid<SelectableType>);
        }
      }
      return uids;
    },
    highlightedUids(state): Set<Uid<HighlightableType>> {
      const uids = new Set<Uid<HighlightableType>>();
      for (const type in state.highlighted) {
        const t = type as HighlightableType;
        if (state.highlighted[t] == null) continue;
        for (const id of state.highlighted[t]) {
          uids.add(`${type}_${id}` as Uid<HighlightableType>);
        }
      }
      return uids;
    },
    selectedItems(state): Identity<SelectableType>[] {
      return collectSelectedItems(state.selected);
    },

    /**
     * if one item is selected, return that
     * if one item is selected and one is fixed, return the selected.
     */
    selectedItem(state): Identity<SelectableType> | null {
      const selectedItems = collectSelectedItems(state.selected);
      if (selectedItems.length === 1) return selectedItems[0];
      return null;
    },

    selectionContains(state) {
      const selected = state.selected;
      return (item: PartialIdentity<SelectableType> | null): boolean => {
        if (item == null) return false;
        const { type, id } = normalizeIdentity(item);

        if (!selected[type]) {
          console.warn("selected does not have", type);
          return false;
        }
        return selected[type].includes(id);
      };
    }
  },
  actions: {
    clear() {
      this.selected = defaultSelected();
      this.subSelection = null;
    },
    clearHighlights() {
      this.highlighted = defaultHighlights();
    },
    clearAll() {
      this.$reset();
      this.selected = defaultSelected();
      this.subSelection = null;
      this.highlighted = defaultHighlights();
    },

    remove(item: { type: SelectableType; id: number }) {
      const selectedOfType = this.selected[item.type];
      if (!selectedOfType) {
        console.warn("no selection store for", item.type);
        return;
      }

      const index = selectedOfType.findIndex((id) => item.id === id);
      if (index !== -1) {
        selectedOfType.splice(index, 1);
        this.subSelection = null;
      }
    },

    removeHighlight({ type, id }: { type: HighlightableType; id: number }) {
      const highlightedOfType = this.highlighted[type];
      if (!highlightedOfType) {
        console.warn("no highlight store for", type);
        return;
      }
      const index = highlightedOfType.indexOf(id);
      if (index > -1) {
        const newHighlighted = highlightedOfType.filter((h) => h !== id);
        this.highlighted[type] = newHighlighted;
      }
    },

    select(item: PartialIdentity<SelectableType> | null) {
      if (this.selectedItems.length === 1 && this.selectionContains(item)) {
        return;
      }
      this.clear();
      if (this.selectionContains(item)) {
        return;
      }
      this.add(item);
    },
    setHighlight(item: { type: HighlightableType; id: number }) {
      if (item?.type == null) {
        console.warn("item to highlight does not have a type.", item);
        return;
      }
      if (!(item.type in this.highlighted)) {
        console.warn(`${item.type} was not in highlight list`);
        this.highlighted[item.type] = [];
      }
      this.highlighted[item.type].push(item.id);
    },

    add(item: PartialIdentity<SelectableType> | null, removeIfInside = false) {
      if (item == null) return;

      const { type, id } = normalizeIdentity(item);

      if (!this.selected[type]) {
        console.warn("no selection store for", type);
        return;
      }

      // only add the item if it is not already selected
      const idIndex = this.selected[type].indexOf(id);
      if (idIndex == -1) {
        this.selected[type].push(id);
      } else if (removeIfInside) {
        this.remove({ type, id });
      }
    },
    addHighlight(item: { type: HighlightableType; id: number }) {
      if (item == null) return;
      const { type, id } = normalizeIdentity(item);
      this.highlighted[type].push(id);
    },

    contains(item: PartialIdentity<SelectableType> | null): boolean {
      if (item == null) return false;
      const { type, id } = normalizeIdentity(item);
      if (!this.selected[type]) {
        console.warn("No selection for", type);
        return false;
      }
      return this.selected[type].includes(id);
    },

    removeDeletedItems() {
      const model = useModelStore();

      // filter items to keep:
      const items = this.selectedItems.filter((item) => {
        if (item == null) return false;
        if (item.type == "question") return true;
        return model.getItem(item?.type, item.id) !== null;
      });

      this.clear();

      // re-add all items
      for (const item of items) {
        this.add(item);
      }
    },

    addStakeholder(item: PartialIdentity<"stakeholder"> | null) {
      if (!item) return;
      const { id } = normalizeIdentity(item);

      if (this.selectedStakeholderIds.includes(id)) return;

      this.selectedStakeholderIds.push(id);
    },
    removeStakeholder(item: PartialIdentity<"stakeholder"> | null) {
      if (!item) return;
      const { id } = normalizeIdentity(item);
      const index = this.selectedStakeholderIds.indexOf(id);
      if (index === -1) return;
      return this.selectedStakeholderIds.splice(index, 1);
    },
    deselectStakeholders() {
      this.selectedStakeholderIds = [];
    },

    /**
     * Delete all the currently selected items
     */
    async deleteSelected() {
      for (const _type in this.selected) {
        const type = _type as SelectableType;
        if (type == null) continue;
        if (type === "question") continue;

        const typeStore = useModelStore().storesByType[type];
        if (!typeStore) {
          console.warn("no store for type", type);
          continue;
        }
        const selectedIds = [...this.selected[type]];

        for (const id of selectedIds) {
          if (id == null) continue;
          await typeStore.delete({ id: id });
        }

        this.subSelection = null;
      }
    },

    selectMany(items: PartialIdentity<SelectableType>[]) {
      this.clear();
      this.$patch((state) => {
        for (let item of items) {
          if (!item) continue;

          const { type, id } = normalizeIdentity(item);

          if (!state.selected[type]) {
            console.warn("no selection store for", type);
            continue;
          }
          state.selected[type].push(id);
        }
      });
    },

    /**
     * Select an item for the sub-selection.
     */
    selectSub(item: PartialIdentity<"stakeholder" | "relation" | "flow" | "flowBranch" | "momentum"> | null) {
      if (!item) {
        this.subSelection = null;
        return;
      }

      const { type, id } = normalizeIdentity(item);

      // deselect the item if it is already selected:
      if (
        this.subSelection?.id === id &&
        this.subSelection?.type === type
      ) {
        this.subSelection = null;
      } else {
        this.subSelection = { type, id };
      }
    },

    selectSubSub(item: PartialIdentity<"flowBranch"> | null) {
      if (!item) {
        this.subSubSelection = null;
        return;
      }

      const { type, id } = normalizeIdentity(item);
      
      // deselect the item if it is already selected:
      if (
        this.subSubSelection?.id === id &&
        this.subSubSelection?.type === type
      ) {
        this.subSubSelection = null;
      } else {
        this.subSubSelection = { type, id };
      }
    }
  }
});

function collectSelectedItems(selected: { [k in SelectableType]: number[] }) {
  const allItems: Identity<SelectableType>[] = [];
  let type: SelectableType;
  for (type in selected) {
    allItems.push(...selected[type].map((id) => normalizeIdentity({ type, id })));
  }
  return allItems;
}

if (import.meta.hot) {
  import.meta.hot.accept(
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    acceptHMRUpdate(useSelectionStore as any, import.meta.hot)
  );
}
